import React from 'react'

import { Input } from 'antd'
import _ from 'lodash'

import {
  RecordLink,
  RecordsSelect
} from 'portal/components/RecordsTable/RecordsColumn'

import { RECORD_STATUS } from 'portal/lib/constants'
import { recordsComponentFactory } from './recordsComponentFactory'
const { Search } = Input

function setCellId(record, rowIndex) {
  if (rowIndex === 0) {
    return {}
  }
  if (record.removed) {
    return {
      id: 'RecordsTableRow',
      style: {
        textDecoration: 'line-through',
        opacity: '0.5'
      }
    }
  }
  return {
    id: 'RecordsTableCell'
  }
}

export const Waste = recordsComponentFactory({
  ModuleName: 'Waste',
  dateField: 'removalDate',
  buildRecordsForDisplay: function (records, projectTimeZone) {
    return {
      data: (records ?? []).map(record => {
        const wasteCodes = record.data.items
          ?.reduce((codes, item) => {
            if (!item.isDeleted && !item.isRemoved && item.wasteCode) {
              codes.push(item.wasteCode)
            }
            return codes
          }, [])
          .join(',\n')
        const knownContainerTypes = record.data.items
          ?.reduce((types, item) => {
            if (!item.isDeleted && !item.isRemoved && item.knownContainerType) {
              types.push(item.knownContainerType)
            }
            return types
          }, [])
          .join(',\n')
        return {
          record: record,
          createdAt: new Date(record.createdAt).toLocaleDateString([], {
            timeZone: projectTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }),
          removalDate: record.data.removalDate
            ? new Date(record.data.removalDate).toLocaleDateString([], {
                timeZone: projectTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            : undefined,
          removalExitDate: record.data.removalExitDate
            ? new Date(record.data.removalExitDate).toLocaleDateString([], {
                timeZone: projectTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            : undefined,
          consignmentId: record.data.consignmentId,
          carrierName: record.data.carrierName,
          originSiteAddressKnown: record.data.originSiteAddressKnown,
          facilityPermitId: record.data.facilityPermitId,
          facilityOperatorName: record.data.facilityOperatorName,
          subcontractorName: record.data.subcontractorName,
          wasteCodes: wasteCodes,
          knownContainerTypes: knownContainerTypes,
          weight:
            _.round(
              record.data.items?.reduce((total, item) => {
                if (!item.isDeleted && !item.isRemoved) {
                  total += item.weight
                    ? item.weight
                    : item.weightEstimated
                      ? item.weightEstimated
                      : 0
                }
                return total
              }, 0),
              3
            ) || '',
          volume:
            _.round(
              record.data.items?.reduce((total, item) => {
                if (!item.isDeleted && !item.isRemoved) {
                  total += item.volume
                    ? item.volume
                    : item.volumeEstimated
                      ? item.volumeEstimated
                      : 0
                }
                return total
              }, 0),
              3
            ) || '',
          recordIdRaw: record.recordId,
          removed: record.status === RECORD_STATUS.REMOVED
        }
      })
    }
  },
  buildColumnsForTable: function (
    { size },
    state,
    fetchOptions,
    assignedToOptions,
    filters,
    updateFilters,
    projectId,
    tableId,
    accountId,
    sortById,
    updateSortById,
    isImperial,
    text,
    fields
  ) {
    const columns = [
      {
        dataIndex: 'createdAt',
        className: 'createdAt',
        title: 'Created At',
        width: '8.125rem',
        onCell: setCellId
      },
      {
        dataIndex: 'removalDate',
        className: 'removalDate',
        title: 'Collection Date',
        width: '9.625rem',
        onCell: setCellId
      },
      (!fields || fields.consignmentId) && {
        dataIndex: 'consignmentId',
        className: 'consignmentId',
        sorter: true,
        ellipsis: true,
        render: (_, record, index) => {
          if (index === 0) {
            return (
              <Search
                defaultValue={filters.consignmentId}
                placeholder={'Note: Case sensitive'}
                onSearch={value => {
                  updateFilters('consignmentId', value, filters)
                }}
                onBlur={e => {
                  if (e.target.value !== filters.consignmentId) {
                    updateFilters('consignmentId', e.target.value)
                  }
                }}
                data-testid={'consignmentIdFilter'}
              />
            )
          } else {
            return record.consignmentId
          }
        },
        title: text.consignmentNo,
        onCell: setCellId
      },
      (!fields || fields.carrierName) &&
        size > 1 && {
          dataIndex: 'carrierName',
          className: 'carrierName',
          ellipsis: true,
          render: (_, record, index) => {
            if (index === 0) {
              return RecordsSelect(
                'carrierName',
                state,
                fetchOptions,
                filters,
                updateFilters,
                projectId,
                tableId
              )
            } else {
              return record.carrierName
            }
          },
          title: text.carrier,
          onCell: setCellId
        },
      (!fields || fields.originSiteAddressKnown) &&
        size > 1 && {
          dataIndex: 'originSiteAddressKnown',
          className: 'originSiteAddressKnown',
          ellipsis: true,
          render: (_, record, index) => {
            if (index === 0) {
              return RecordsSelect(
                'originSiteAddressKnown',
                state,
                fetchOptions,
                filters,
                updateFilters,
                projectId,
                tableId
              )
            } else {
              return record.originSiteAddressKnown
            }
          },
          title: 'Origin Site Address',
          onCell: setCellId
        },
      (!fields || fields.facilityPermitId) &&
        size > 1 && {
          dataIndex: 'facilityPermitId',
          className: 'facilityPermitId',
          ellipsis: true,
          render: (_, record, index) => {
            if (index === 0) {
              return RecordsSelect(
                'facilityPermitId',
                state,
                fetchOptions,
                filters,
                updateFilters,
                projectId,
                tableId
              )
            } else {
              return record.facilityPermitId
            }
          },
          title: 'Facility Permit',
          onCell: setCellId
        },
      (!fields || fields.facilityOperatorName) &&
        size > 1 && {
          dataIndex: 'facilityOperatorName',
          className: 'facilityOperatorName',
          ellipsis: true,
          render: (_, record, index) => {
            if (index === 0) {
              return RecordsSelect(
                'facilityOperatorName',
                state,
                fetchOptions,
                filters,
                updateFilters,
                projectId,
                tableId
              )
            } else {
              return record.facilityOperatorName
            }
          },
          title: 'Facility Operator',
          onCell: setCellId
        },
      (!fields || fields.subcontractorName) &&
        size > 1 && {
          dataIndex: 'subcontractorName',
          className: 'subcontractorName',
          ellipsis: true,
          render: (_, record, index) => {
            if (index === 0) {
              return RecordsSelect(
                'subcontractorName',
                state,
                fetchOptions,
                filters,
                updateFilters,
                projectId,
                tableId
              )
            } else {
              return record.subcontractorName
            }
          },
          title: 'Subcontractor',
          onCell: setCellId
        },
      (!fields || fields.wasteCode) &&
        size > 1 && {
          dataIndex: 'wasteCode',
          className: 'wasteCode',
          ellipsis: true,
          render: (_, record, index) => {
            if (index === 0) {
              return RecordsSelect(
                'items[].wasteCode',
                state,
                fetchOptions,
                filters,
                updateFilters,
                projectId,
                tableId
              )
            } else {
              return record.wasteCodes
            }
          },
          title: 'Waste Code (EWC)',
          width: '10.25rem',
          onCell: setCellId
        },
      (!fields || fields.knownContainerType) &&
        size > 1 && {
          dataIndex: 'knownContainerType',
          className: 'knownContainerType',
          ellipsis: true,
          render: (_, record, index) => {
            if (index === 0) {
              return RecordsSelect(
                'items[].knownContainerType',
                state,
                fetchOptions,
                filters,
                updateFilters,
                projectId,
                tableId
              )
            } else {
              return record.knownContainerTypes
            }
          },
          title: 'Container Type',
          onCell: setCellId
        },
      (!fields || fields.weight) &&
        size > 2 && {
          dataIndex: 'weight',
          className: 'weight',
          title: isImperial ? (
            <span title="Weight">
              Weight (<span className="lowercase">lbs</span>)
            </span>
          ) : (
            <span title="Weight">
              Weight (<span className="lowercase">t</span>)
            </span>
          ),
          width: '7.125rem',
          onCell: setCellId
        },
      (!fields || fields.volume) &&
        size > 2 && {
          dataIndex: 'volume',
          className: 'volume',
          title: isImperial ? (
            <span title="Volume">
              Volume (<span className="lowercase">cu yd</span>)
            </span>
          ) : (
            <span title="Volume">
              Volume (
              <span className="lowercase">
                m<sup>3</sup>
              </span>
              )
            </span>
          ),
          width: '8.375rem',
          onCell: setCellId
        },
      size > 1 && {
        title: 'Assigned To',
        ellipsis: false,
        render: (_, record, index) => {
          if (index === 0) {
            return RecordsSelect(
              'assignedTo',
              state,
              assignedToOptions,
              filters,
              updateFilters,
              projectId,
              tableId
            )
          } else {
            return record.assignedTo
          }
        },
        dataIndex: 'assignedTo',
        className: 'assignedTo',
        width: '8rem',
        align: 'center'
      },
      RecordLink(setCellId, filters, updateFilters)
    ]

    return columns
  },
  initialFilterState: {
    'consignmentId': '',
    'removalDate': '',
    'carrierName': [],
    'originSiteAddressKnown': [],
    'facilityOperatorName': [],
    'subcontractorName': [],
    'items[].knownContainerType': [],
    'items[].wasteCode': []
  }
})
